import gql from "graphql-tag";
import type { DocumentNode } from "graphql";
import { shallowReactive, type InjectionKey } from "vue";
import {
  apolloErrorList,
  isProductMediaValidationError,
  markProductMediaUnsupported,
  normalizeLqip,
  resolveProductMediaSupport,
} from "./productMedia";

// Catalog cards' blurred previews (owner, 2026-10-21, variant B of
// shared/docs/reports/2026-10/CATALOG-LQIP-COST-2026-10-21.uk.md): the tiny
// WebP of each card photo (`media.lqip`, the main photo and the two card
// slides) shows under a photo that is not ready yet, and the photo fades in
// over it, as in the PDP gallery. The listing queries keep the card selection
// without `lqip` (PRODUCT_CARD_MEDIA_FIELDS), so the server HTML does not
// grow: the previews come from this separate request, in the browser only,
// once the page has loaded (first page) or at once (a new result list).

/** Provided by organisms/catalog-product: its cards show previews. */
export const CATALOG_CARD_LQIP_KEY: InjectionKey<boolean> =
  Symbol("catalogCardLqip");

export const CATALOG_CARD_LQIP_QUERY = gql`
  query CatalogCardLqip($ids: [Int], $size: Int) {
    productsList(page: 1, size: $size, ids: $ids) {
      content {
        id
        media {
          jpeg {
            src
          }
          lqip
        }
        images(limit: 2) {
          __typename
          media {
            jpeg {
              src
            }
            lqip
          }
        }
      }
    }
  }
`;

const BATCH = 48;
// About 0.4 KB per preview: enough for several result pages of three photos.
const MAX_PHOTOS = 600;

// A photo is known by its `media` JPEG URL: it carries the photo's version
// (etag), and the API's answer order is not the order of the ids asked for.
const previews = shallowReactive(new Map<string, string>());
// Photos the API answered without a preview: not asked for again.
const withoutPreview = new Set<string>();
const pending = new Set<string>();

const photoKey = (media: any): string => {
  const src = media?.jpeg?.src;
  return typeof src === "string" && src.length > 0 ? src : "";
};

const cardPhotoKeys = (product: any): string[] =>
  [
    product?.media,
    ...(Array.isArray(product?.images)
      ? product.images.slice(0, 2).map((image: any) => image?.media)
      : []),
  ]
    .map(photoKey)
    .filter(Boolean);

// Oldest first out once full (a Map and a Set keep insertion order).
const makeRoom = (map: Map<string, unknown> | Set<string>) => {
  if (map.size < MAX_PHOTOS) return;
  const oldest = map.keys().next().value;
  if (oldest !== undefined) map.delete(oldest);
};

const store = (media: any) => {
  const key = photoKey(media);
  const lqip = normalizeLqip(media?.lqip);
  if (!key || !lqip) return;
  makeRoom(previews);
  previews.set(key, lqip);
};

const markWithoutPreview = (key: string) => {
  if (previews.has(key) || withoutPreview.has(key)) return;
  makeRoom(withoutPreview);
  withoutPreview.add(key);
};

/** The preview of a card photo (its own `media`), or "" while unknown. */
export const catalogCardLqip = (media: unknown): string =>
  previews.get(photoKey(media)) || "";

const needsPreview = (product: any) =>
  cardPhotoKeys(product).some(
    (key) => !previews.has(key) && !withoutPreview.has(key)
  );

// After the load event, when the browser is idle: the first page's photos
// (the LCP among them) never share the network with this request.
const afterPageLoad = () =>
  new Promise<void>((resolve) => {
    const idle = () => {
      const ric = (window as any).requestIdleCallback;
      if (typeof ric === "function") ric(() => resolve(), { timeout: 2000 });
      else window.setTimeout(resolve, 200);
    };
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });
  });

export type CatalogCardLqipOptions = { afterLoad?: boolean };

/**
 * Asks for the previews of these products' card photos that are not known
 * yet, 48 products per request, one request at a time. Browser only; never
 * rejects: a failed request leaves the cards as they are (white until the
 * photo arrives) and its products may be asked for again with the next list.
 */
export async function requestCatalogCardLqip(
  client: any,
  products: readonly any[] | null | undefined,
  options: CatalogCardLqipOptions = {}
): Promise<void> {
  if (typeof window === "undefined" || typeof client?.query !== "function")
    return;
  if (options.afterLoad) await afterPageLoad();
  // Each product asked for, with the card photos this list shows for it.
  const asked = new Map<string, string[]>();
  for (const product of Array.isArray(products) ? products : []) {
    const id = product?.id != null ? String(product.id) : "";
    if (!/^\d+$/.test(id) || pending.has(id) || asked.has(id)) continue;
    if (needsPreview(product)) asked.set(id, cardPhotoKeys(product));
  }
  const ids = [...asked.keys()];
  if (!ids.length) return;
  ids.forEach((id) => pending.add(id));
  try {
    // The media gate (utils/productMedia), kept here rather than through
    // sendWithProductMedia: the document goes out only while the gate is
    // open, and a failure of this enhancement must not close the gate that
    // the listing and PDP queries read (their photos would fall back to the
    // legacy ones), nor send a resend without `media`. Only a schema refusal
    // for `media` reaches the gate: it is the schema's answer for every
    // query, not this request's bad luck.
    if (!(await resolveProductMediaSupport(client))) return;
    for (let start = 0; start < ids.length; start += BATCH) {
      const batch = ids.slice(start, start + BATCH);
      const send = (query: DocumentNode) =>
        client.query({
          query,
          variables: { ids: batch.map(Number), size: batch.length },
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        });
      let response: any;
      try {
        response = await send(CATALOG_CARD_LQIP_QUERY);
      } catch (error) {
        if (isProductMediaValidationError(apolloErrorList(error)))
          markProductMediaUnsupported();
        return;
      }
      if (isProductMediaValidationError(response?.errors)) {
        markProductMediaUnsupported();
        return;
      }
      const content = response?.data?.productsList?.content;
      if (!Array.isArray(content)) continue;
      for (const product of content) {
        store(product?.media);
        if (Array.isArray(product?.images))
          product.images
            .slice(0, 2)
            .forEach((image: any) => store(image?.media));
      }
      // A photo of this batch that came back without a preview (no `lqip`,
      // another version, a product missing from the answer) is not asked
      // for again.
      batch.forEach((id) => asked.get(id)?.forEach(markWithoutPreview));
    }
  } catch {
    // Previews are an enhancement: the cards keep today's behaviour.
  } finally {
    ids.forEach((id) => pending.delete(id));
  }
}

/** Test hook: forget every preview. */
export function resetCatalogCardLqip(): void {
  previews.clear();
  withoutPreview.clear();
  pending.clear();
}
